import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ObservationSearch = () => {
  const navigate = useNavigate();
  const { user, updateUserPreferences } = useAuth();
  const [targetName, setTargetName] = useState('');
  const [status, setStatus] = useState('');
  const [observatoryId, setObservatoryId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [observatories, setObservatories] = useState([]);
  const [projects, setProjects] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [obsResp, projResp] = await Promise.all([
          apiService.getObservatories(),
          apiService.getAllProjects()
        ]);
        const obsList = obsResp.data?.observatories || [];
        setObservatories(obsList);
        // getAllProjects resolves to a flat array (walks every page).
        setProjects(Array.isArray(projResp) ? projResp : []);

        // Pre-select saved observatory preference
        const savedObsId = user?.preferences?.last_observatory_id;
        if (savedObsId && obsList.find(o => o._id === savedObsId)) {
          setObservatoryId(savedObsId);
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = useCallback(async () => {
    if (!targetName || targetName.length < 2) {
      setResults([]);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = {
        q: targetName,
        page: page + 1,
        limit: rowsPerPage,
      };
      if (status) params.status = status;
      if (observatoryId) params.observatory_id = observatoryId;
      if (projectId) params.project_id = projectId;
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await apiService.searchObservations(params);
      if (response.success) {
        setResults(response.data.observations);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      const msg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : err.response?.data?.error?.message || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [targetName, status, observatoryId, projectId, startDate, endDate, page, rowsPerPage]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(doSearch, 400);
    return () => clearTimeout(timer);
  }, [doSearch]);

  const clearFilters = () => {
    setTargetName('');
    setStatus('');
    setObservatoryId('');
    setProjectId('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
    setResults([]);
    setTotal(0);
  };

  const statusColors = {
    completed: 'success',
    completed_series: 'success',
    partially_completed: 'secondary',
    failed: 'error',
    in_progress: 'warning',
    assigned: 'info',
    acquisition_complete: 'info',
    pending: 'default',
    waiting_next: 'info',
    cancelled: 'default',
    aborted: 'error',
    expired: 'error',
    paused: 'warning',
    suspended: 'warning',
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Search Observations</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={doSearch}>
          Refresh
        </Button>
      </Box>

      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Target Name"
            value={targetName}
            onChange={(e) => { setTargetName(e.target.value); setPage(0); }}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
              endAdornment: targetName && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setTargetName('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} label="Status">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="aborted">Aborted</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Observatory</InputLabel>
            <Select value={observatoryId} onChange={(e) => { const v = e.target.value; setObservatoryId(v); setPage(0); if (v) updateUserPreferences({ last_observatory_id: v }); }} label="Observatory">
              <MenuItem value="">All</MenuItem>
              {observatories.map(o => (
                <MenuItem key={o._id} value={o._id}>{o.name || o.code}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Project</InputLabel>
            <Select value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(0); }} label="Project">
              <MenuItem value="">All</MenuItem>
              {projects.map(p => (
                <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={(d) => { setStartDate(d); setPage(0); }}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <DatePicker
              label="To"
              value={endDate}
              onChange={(d) => { setEndDate(d); setPage(0); }}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
          </LocalizationProvider>

          <Button variant="text" onClick={clearFilters}>Clear All</Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Results table */}
      <Paper>
        {loading && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Target</TableCell>
                <TableCell>Observation Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Observatory</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((obs) => (
                <TableRow
                  key={obs._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/observations/${obs._id}`)}
                >
                  <TableCell>{obs.targets?.[0]?.name || 'Unknown'}</TableCell>
                  <TableCell>{obs.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={obs.status}
                      size="small"
                      color={statusColors[obs.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{obs.assigned_to?.name || 'Unassigned'}</TableCell>
                  <TableCell>{obs.created_at ? new Date(obs.created_at).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{obs.completed_at ? new Date(obs.completed_at).toLocaleDateString() : ''}</TableCell>
                </TableRow>
              ))}
              {!loading && results.length === 0 && targetName.length >= 2 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No observations found matching "{targetName}"</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && targetName.length < 2 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">Enter at least 2 characters to search</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </Paper>
    </Box>
  );
};

export default ObservationSearch;
